import { defineStore } from "pinia";
import { getShipments, ApiWebBillOfLadingsPermissions, postDocumentsDownload } from "@/lib/api";
import dayjs from "dayjs";
import { filters } from "@/store/filters";
import { parseResponseAsShipments } from "@/lib/parseResponseAsShipments";
import { dateComparator, trackData, getOptionsFromLocalStorage } from "@/lib/utilities";
import { getCustomerCodeFromJwt } from "@maersk-global/shared-js";
import CONSTANTS from "@/lib/CONSTANTS";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export const documents = defineStore("documents", {
  state: () => ({
    documentsAllowed: {
      verifyCopy: [],
      billOfLading: [],
      waybill: []
    },
    allDocuments: [],
    allShipments: [],
    documentsOnPage: [],
    shipmentsError: false,
    showSpinner: true,
    paginationToken: 1,
    lastPage: null,
    totalCount: "",
    timeFrameValue: "next_7_days",
    selectedDocumentType: "all",
    hasSignedBLAgreement: false,
    webBlAgreementError: false,
    docDownloadFailed2xx: false,
    docDownloadFailed5xx: false
  }),
  actions: {
    async getDocuments(timeFrame) {
      try {
        this.showSpinner = true;
        const shipmentsData = [];
        const queryParams = {
          departingFromDateUtc: this.getFromDate(timeFrame),
          departingToDateUtc: this.getToDate(timeFrame),
          page: this.paginationToken,
          sort: "etdUtcAtPortOfLoading:asc",
          ...filters().getGlobalFilters,
          limit: 25,
          taskCode: "0000000000081,0000000000083",
          statusCode: 4,
          customerPartyRoleCodes: "15,22"
        };
        const firstResponse = await getShipments(queryParams, "tasks, parties");
        let nextPage = Number(firstResponse.headers["next-page"]);
        shipmentsData.push(...firstResponse.data);
        if (nextPage) {
          while (nextPage) {
            queryParams.page = nextPage;
            const res = await getShipments(queryParams, "tasks, parties");
            shipmentsData.push(...res.data);
            nextPage = res.headers["next-page"];
          }
        }
        this.allShipments = parseResponseAsShipments(
          shipmentsData.sort((a, b) => dateComparator(a.etdUtcAtPlaceOfReceipt, b.etdUtcAtPlaceOfReceipt, null))
        );
        this.prepareAllowedDocuments();
        this.updateDocuments();
        this.setPaginatedDocuments();
      } catch (e) {
        if (e?.status === 404) {
          this.allShipments = [];
          this.totalCount = 0;
          this.lastPage = 0;
          this.shipmentsError = false;
        } else {
          this.shipmentsError = true;
          trackData("documentsListError", { error: e }, { error: JSON.stringify(e) });
        }
      } finally {
        this.showSpinner = false;
      }
    },
    getFromDate(timeFrame) {
      switch (timeFrame) {
        case "last_3_days":
          return dayjs().utc().subtract(3, "days").format("YYYY-MM-DD");
        case "last_7_days":
          return dayjs().utc().subtract(7, "days").format("YYYY-MM-DD");
        case "last_month":
          return dayjs().utc().subtract(1, "month").format("YYYY-MM-DD");
        case "last_3_months":
          return dayjs().utc().subtract(3, "month").format("YYYY-MM-DD");
        default:
          return dayjs().utc().format("YYYY-MM-DD");
      }
    },
    getToDate(timeFrame) {
      switch (timeFrame) {
        case "next_3_days":
          return dayjs().utc().add(3, "days").format("YYYY-MM-DD");
        case "next_7_days":
          return dayjs().utc().add(7, "days").format("YYYY-MM-DD");
        case "next_month":
          return dayjs().utc().add(1, "month").format("YYYY-MM-DD");
        case "next_3_months":
          return dayjs().utc().add(3, "month").format("YYYY-MM-DD");
        default:
          return dayjs().utc().format("YYYY-MM-DD");
      }
    },
    async getPermissions() {
      try {
        const permissions = await ApiWebBillOfLadingsPermissions();
        this.hasSignedBLAgreement = permissions?.[0]?.negotiableBL;
      } catch (e) {
        this.webBlAgreementError = true;
      }
    },
    updatePaginationToken(pageToken) {
      this.paginationToken = pageToken;
      this.setPaginatedDocuments();
    },
    updateDocuments() {
      this.allDocuments = [];
      this.allShipments.forEach((shipment) => {
        for (const [documentType, shipmentNumbers] of Object.entries(this.documentsAllowed)) {
          if (shipmentNumbers?.indexOf(shipment.ladingNumber) !== -1) {
            this.allDocuments.push({ ...shipment, shipmentNumber: shipment.ladingNumber, documentType });
          }
        }
      });
    },
    prepareAllowedDocuments() {
      this.allShipments.forEach((shipment) => {
        let verifyCopyConditionOne = false;
        let verifyCopyConditionTwo = true;

        let originalBoLConditionOne = false;
        let originalBoLConditionTwo = false;

        let wayBillConditionOne = false;
        let wayBillConditionTwo = true;

        shipment.tasks.forEach((task) => {
          // Issue Verify Copy task
          if (task.taskCode === "0000000000081" && task.statusCode === "4") {
            verifyCopyConditionOne = true;
          }
          // Issue Original Transport Document task
          if (task.taskCode === "0000000000083" && task.statusCode === "4") {
            verifyCopyConditionTwo = false;
            originalBoLConditionOne = true;
            wayBillConditionOne = true;
          }
          // Mark Original Transport Document as Received
          if (task.taskCode === "0000000000069") {
            originalBoLConditionTwo = true;
            wayBillConditionTwo = false;
          }
        });
        if (verifyCopyConditionOne && verifyCopyConditionTwo)
          this.documentsAllowed.verifyCopy.push(shipment.ladingNumber);
        if (originalBoLConditionOne && originalBoLConditionTwo)
          this.documentsAllowed.billOfLading.push(shipment.ladingNumber);
        if (wayBillConditionOne && wayBillConditionTwo) this.documentsAllowed.waybill.push(shipment.ladingNumber);
      });
    },
    updateSelectedDocumentType(type) {
      this.selectedDocumentType = type;
      this.setPaginatedDocuments();
      localStorage.setItem("documentTypeFilterSO", type);
    },
    setPaginatedDocuments() {
      const startPage = (this.paginationToken - 1) * 25;
      const endPage = startPage + 25;
      if (this.selectedDocumentType === "all") {
        this.documentsOnPage = this.allDocuments.slice(startPage, endPage);
        this.totalCount = this.allDocuments.length;
        this.lastPage = Math.ceil(this.allDocuments.length / 25);
      } else {
        const filteredDocuments = this.allDocuments.filter((item) => item.documentType === this.selectedDocumentType);
        this.totalCount = filteredDocuments.length;
        this.lastPage = Math.ceil(filteredDocuments.length / 25);
        this.documentsOnPage = filteredDocuments.slice(startPage, endPage);
      }
    },
    async downloadDocument(documentType, shipmentNumber, parties) {
      const doc = [
        {
          documentType,
          bolNumber: shipmentNumber,
          organizationName: this.organizationName(),
          gcssDocument: false
        }
      ];
      try {
        // Get available documentIds to download
        const response = await postDocumentsDownload(doc);
        if (response && response[0] && response[0]["pdfContent"]) {
          const linkSource = `data:application/pdf;base64,${response[0]["pdfContent"]}`;
          const link = document.createElement("a");
          link.href = linkSource;
          link.download = response[0]["documentName"];
          link.click();
          this.trackDownloadDataOrbit("DownloadSuccess", shipmentNumber, documentType, parties);
          this.trackDownloadDataPensieve("SO_Documents", shipmentNumber, documentType, parties);
        } else {
          this.docDownloadFailed2xx = shipmentNumber;
          this.documentLinks = {};
          this.trackDownloadDataOrbit("DownloadWarning", shipmentNumber, documentType, parties);
          this.trackDownloadDataPensieve("SO_Documents_Error", shipmentNumber, documentType, parties);
        }
      } catch (error) {
        this.docDownloadFailed5xx = shipmentNumber;
        this.documentLinks = {};
        this.trackDownloadDataOrbit("DownloadError", shipmentNumber, documentType, parties);
        this.trackDownloadDataPensieve("SO_Documents_Error", shipmentNumber, documentType, parties);
      }
    },
    trackDownloadDataOrbit(action, bolNumber, documentType, parties) {
      window.dataLayer?.push({
        event: "DQCS",
        category: "SD_Download",
        action: "Click",
        label: {
          data: action,
          tpDocNo: bolNumber,
          document: CONSTANTS.GA_DOCUMENT_MAP[documentType],
          app: "SO",
          party: JSON.stringify(
            parties.map((party) => {
              return party["partyRoleTypeName"] || "Unmapped";
            })
          )
        }
      });
    },
    trackDownloadDataPensieve(action, bolNumber, documentType, parties) {
      trackData(action, {
        tpDocNo: String(bolNumber),
        document: CONSTANTS.GA_DOCUMENT_MAP[documentType],
        app: "SO",
        party: JSON.stringify(
          parties.map((party) => {
            return party["partyRoleTypeName"] || "Unmapped";
          })
        )
      });
    },
    organizationName() {
      return (
        this.documentsOnPage
          ?.flatMap((shipment) => {
            return shipment?.parties.map((party) => {
              return { id: party.id, name: party.name };
            });
          })
          .find((item) => item.id === getCustomerCodeFromJwt())?.name || null
      );
    },
    checkTimeFrameFromLocalStorage() {
      const timeFrames = [
        "next_3_days",
        "next_7_days",
        "next_month",
        "next_3_months",
        "last_3_days",
        "last_7_days",
        "last_month",
        "last_3_months"
      ];

      this.timeFrameValue = getOptionsFromLocalStorage(timeFrames, "documentsTimeFrameSO") || "next_7_days";
    }
  }
});
